<template>
    <div class="chart" ref="chartdiv"></div>
</template>

<script>

    import {mapState, mapMutations, mapGetters, mapActions} from 'vuex';
    import * as am4core from "@amcharts/amcharts4/core";
    import * as am4charts from "@amcharts/amcharts4/charts";
    import themeDark from "@amcharts/amcharts4/themes/dark";
    import {statusColorMixin} from "../../../components/mixins/statusColorMixin";

    am4core.useTheme(themeDark);

    export default {

        mixins: [statusColorMixin],

        props: ['name'],

        data() {
            return {
                chart: null,
                chartsLib: null,
            }
        },

        mounted() {
            this.loadData();
        },

        beforeDestroy() {
            if (this.chart) {
                this.chart.dispose();
            }
        },

        computed: {
            ...mapState('DashBoard', [
                'chartLoading',
                'projectActivityStats'
            ]),
        },

        methods: {

            setData() {
                if (this.chart) this.chart.data = this.projectActivityStats;
            },

            createChart() {

                let chart = am4core.create(this.$refs.chartdiv, am4charts.XYChart);

                chart.paddingRight = 20;
                chart.paddingLeft = 0;
                chart.paddingBottom = 0;
                chart.paddingTop=10;

                chart.data = this.projectActivityStats;
                chart.cursor = new am4charts.XYCursor();

                chart.colors.list = [
                    am4core.color(this.statusColors.started), // Starts
                    am4core.color(this.statusColors.quality), // Quality
                    am4core.color(this.statusColors.duplicate), // Duplicate
                    am4core.color(this.statusColors.security), // Security
                    am4core.color(this.statusColors.quota), // Quota
                    am4core.color(this.statusColors.profile), // Profile
                    am4core.color(this.statusColors.completed), // Completed
                    am4core.color(this.statusColors.invited), // Invited
                    //am4core.color(this.statusColor.reminded), // Reminded
                    am4core.color(this.statusColors.opened)  // Opened
                ];

                let categoryAxis = chart.xAxes.push(new am4charts.DateAxis());
                categoryAxis.renderer.grid.template.location = 0;
                categoryAxis.baseInterval.timeUnit = "day";
                categoryAxis.baseInterval.count = 5;

                let valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
                valueAxis.tooltip.disabled = true;
                valueAxis.renderer.minWidth = 25;

                let seriesInvited = chart.series.push(new am4charts.ColumnSeries());
                seriesInvited.dataFields.valueY = "invited";
                seriesInvited.dataFields.dateX = "period";
                seriesInvited.tooltipText = "{valueY.value}";
                seriesInvited.columns.template.fillOpacity = 0.5;
                seriesInvited.name = "Invited";
                seriesInvited.tooltipText = "{name}: [bold]{valueY}[/]";
                seriesInvited.stacked = true;
                seriesInvited.showOnInit = false;

                let seriesQuality = chart.series.push(new am4charts.ColumnSeries());
                seriesQuality.dataFields.valueY = "quality";
                seriesQuality.dataFields.dateX = "period";
                seriesQuality.tooltipText = "{valueY.value}";
                seriesQuality.columns.template.fillOpacity = 0.5;
                seriesQuality.name = "Quality";
                seriesQuality.tooltipText = "{name}: [bold]{valueY}[/]";
                seriesQuality.stacked = true;
                seriesQuality.showOnInit = false;
                seriesQuality.tooltip.label.adapter.add("text", function(text, target) {
                    return target.dataItem && target.dataItem.valueY === 0 ? "" : text
                });

                let seriesDuplicate = chart.series.push(new am4charts.ColumnSeries());
                seriesDuplicate.dataFields.valueY = "duplicate";
                seriesDuplicate.dataFields.dateX = "period";
                seriesDuplicate.tooltipText = "{valueY.value}";
                seriesDuplicate.columns.template.fillOpacity = 0.5;
                seriesDuplicate.name = "Duplicate";
                seriesDuplicate.tooltipText = "{name}: [bold]{valueY}[/]";
                seriesDuplicate.stacked = true;
                seriesDuplicate.showOnInit = false;
                seriesDuplicate.tooltip.label.adapter.add("text", function(text, target) {
                    return target.dataItem && target.dataItem.valueY === 0 ? "" : text
                });

                let seriesSecurity = chart.series.push(new am4charts.ColumnSeries());
                seriesSecurity.dataFields.valueY = "security";
                seriesSecurity.dataFields.dateX = "period";
                seriesSecurity.tooltipText = "{valueY.value}";
                seriesSecurity.columns.template.fillOpacity = 0.5;
                seriesSecurity.name = "Security";
                seriesSecurity.tooltipText = "{name}: [bold]{valueY}[/]";
                seriesSecurity.stacked = true;
                seriesSecurity.showOnInit = false;
                seriesSecurity.tooltip.label.adapter.add("text", function(text, target) {
                    return target.dataItem && target.dataItem.valueY === 0 ? "" : text
                });

                let seriesQuota = chart.series.push(new am4charts.ColumnSeries());
                seriesQuota.dataFields.valueY = "quota";
                seriesQuota.dataFields.dateX = "period";
                seriesQuota.tooltipText = "{valueY.value}";
                seriesQuota.columns.template.fillOpacity = 0.5;
                seriesQuota.name = "Quota";
                seriesQuota.tooltipText = "{name}: [bold]{valueY}[/]";
                seriesQuota.stacked = true;
                seriesQuota.showOnInit = false;
                seriesQuota.tooltip.label.adapter.add("text", function(text, target) {
                    return target.dataItem && target.dataItem.valueY === 0 ? "" : text
                });

                let seriesProfile = chart.series.push(new am4charts.ColumnSeries());
                seriesProfile.dataFields.valueY = "profile";
                seriesProfile.dataFields.dateX = "period";
                seriesProfile.tooltipText = "{valueY.value}";
                seriesProfile.columns.template.fillOpacity = 0.5;
                seriesProfile.name = "Profile";
                seriesProfile.tooltipText = "{name}: [bold]{valueY}[/]";
                seriesProfile.stacked = true;
                seriesProfile.showOnInit = false;
                seriesProfile.tooltip.label.adapter.add("text", function(text, target) {
                    return target.dataItem && target.dataItem.valueY === 0 ? "" : text
                });

                let seriesCompleted = chart.series.push(new am4charts.ColumnSeries());
                seriesCompleted.dataFields.valueY = "completed";
                seriesCompleted.dataFields.dateX = "period";
                seriesCompleted.tooltipText = "{valueY.value}";
                seriesCompleted.columns.template.fillOpacity = 0.5;
                seriesCompleted.name = "Completed";
                seriesCompleted.tooltipText = "{name}: [bold]{valueY}[/]";
                seriesCompleted.stacked = true;
                seriesCompleted.showOnInit = false;

                let seriesStarted = chart.series.push(new am4charts.LineSeries());
                seriesStarted.dataFields.valueY = "started";
                seriesStarted.dataFields.dateX = "period";
                seriesStarted.tooltipText = "{valueY.value}";
                seriesStarted.tensionX = 0.8;
                seriesStarted.tensionY = 0.8;
                seriesStarted.name = "Started"
                seriesStarted.tooltipText = "{name}: [bold]{valueY}[/]";
                seriesStarted.strokeWidth=2;
                seriesStarted.strokeOpacity=1;
                seriesStarted.showOnInit = false;

                // let seriesReminded = chart.series.push(new am4charts.LineSeries());
                // seriesReminded.showOnInit = false;
                // seriesReminded.name = "Reminded";
                // seriesReminded.tensionY = 0.9;
                // seriesReminded.tensionX = 0.9;
                // seriesReminded.dataFields.valueY = "reminded";
                // seriesReminded.dataFields.dateX = "period";
                // seriesReminded.tooltipText = "{name}: [bold]{valueY}[/]";
                // seriesReminded.stacked = false;
                // seriesReminded.strokeWidth = 2;
                // seriesReminded.tooltip.label.adapter.add("text", function(text, target) {
                //     return target.dataItem && target.dataItem.valueY === 0 ? "" : text
                // });

                let seriesOpened = chart.series.push(new am4charts.LineSeries());
                seriesOpened.dataFields.valueY = "opened";
                seriesOpened.dataFields.dateX = "period";
                seriesOpened.tooltipText = "{valueY.value}";
                seriesOpened.tensionX = 0.8;
                seriesOpened.tensionY = 0.8;
                seriesOpened.name = "Opened"
                seriesOpened.tooltipText = "{name}: [bold]{valueY}[/]";
                seriesOpened.strokeWidth=2;
                seriesOpened.strokeOpacity=0.7;
                seriesOpened.showOnInit = false;
                seriesOpened.tooltip.label.adapter.add("text", function(text, target) {
                    return target.dataItem && target.dataItem.valueY === 0 ? "" : text
                });


                this.chart = chart;
            },

            loadData(force = false) {

                this.$emit('loading-started');

                this.$store.dispatch('DashBoard/getProjectActivityStatsDaily', force)
                    .then(() => {
                        this.$emit('loading-ended');

                        if (this.chart===null) this.createChart();
                        this.setData();
                    })
                    .catch(error => {
                        Bus.$emit('flash-message', {type: 'error', message: 'Project activity statistics failed to load!'});
                        this.$emit('loading-ended');
                    });
            }
        },

        created() {
            this.$emit('settings', {title: 'Projects', info: 'Status rates per date', configs: []});

            Bus.$on('refresh-widget-' + this.name, () => {
                this.loadData(true);
            });

        },

    }
</script>

<style scoped>
    .chart {
        width: 100%;
        height: 325px;
    }
</style>
